import React, { useState, useEffect, useCallback, useMemo } from 'react';
import timeTrackingService from '../services/timeTrackingService';
import TimeTrackingNav from '../components/TimeTrackingNav';
import { usePermissions } from '../context/PermissionContext';

function fmtDate(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
}
function fmtTime(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' });
}
function toLocalDatetimeInput(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function entryDateKey(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // YYYY-MM-DD
}
function adjustedByName(e) {
  const u = e.AdjustedBy;
  if (!u) return 'Unknown';
  return (u.firstName || u.lastName) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : (u.username || 'Unknown');
}

const fmtD = d => d.toISOString().split('T')[0];

function getPeriodDates(payPeriodType, offset = 0) {
  const now = new Date();

  if (payPeriodType === 'monthly') {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    return { from: fmtD(new Date(d.getFullYear(), d.getMonth(), 1)), to: fmtD(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
  }
  if (payPeriodType === 'weekly') {
    const day = now.getDay() === 0 ? 6 : now.getDay() - 1;
    const monday = new Date(now); monday.setDate(now.getDate() - day + offset * 7);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    return { from: fmtD(monday), to: fmtD(sunday) };
  }
  if (payPeriodType === 'bi_weekly') {
    const anchor = new Date(now.getFullYear(), 0, 1);
    const daysSince = Math.floor((now - anchor) / 86400000);
    const periodIndex = Math.floor(daysSince / 14) + offset;
    const start = new Date(anchor); start.setDate(anchor.getDate() + periodIndex * 14);
    const end   = new Date(start);  end.setDate(start.getDate() + 13);
    return { from: fmtD(start), to: fmtD(end) };
  }
  // semi_monthly
  const half = now.getDate() <= 15 ? 0 : 1;
  const totalHalves = now.getFullYear() * 24 + now.getMonth() * 2 + half + offset;
  const yr  = Math.floor(totalHalves / 24);
  const mo  = Math.floor((totalHalves % 24) / 2);
  const isFirst = totalHalves % 2 === 0;
  const start = new Date(yr, mo, isFirst ? 1 : 16);
  const endDay = isFirst ? 15 : new Date(yr, mo + 1, 0).getDate();
  return { from: fmtD(start), to: fmtD(new Date(yr, mo, endDay)) };
}


export default function TimeTrackingTimecardsPage() {
  const { companySettings } = usePermissions();
  const payPeriodType = companySettings?.payPeriodType || 'semi_monthly';

  const [employees, setEmployees]   = useState([]);
  const [entries, setEntries]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [empId, setEmpId]           = useState('');
  const [openOnly, setOpenOnly]     = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const [from, setFrom] = useState(() => getPeriodDates('semi_monthly', 0).from);
  const [to, setTo]     = useState(today);

  const [editModal, setEditModal]   = useState(null);
  const [newModal, setNewModal]     = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm]             = useState({});
  const [saving, setSaving]         = useState(false);
  const [saveErr, setSaveErr]       = useState('');
  const [empSearch, setEmpSearch]   = useState('');
  const [empDropOpen, setEmpDropOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (empId) params.employeeId = empId;
      if (from)  params.from = from;
      if (to)    params.to   = to;
      if (openOnly) params.openOnly = 'true';
      const data = await timeTrackingService.getEntries(params);
      setEntries(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [empId, from, to, openOnly]);

  useEffect(() => {
    timeTrackingService.getEmployees().then(setEmployees).catch(() => {});
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Detect employee+day combinations with more than one entry
  const duplicateDayKeys = useMemo(() => {
    const counts = {};
    entries.forEach(e => {
      const key = `${e.employeeId}-${entryDateKey(e.clockIn)}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return new Set(Object.entries(counts).filter(([, v]) => v > 1).map(([k]) => k));
  }, [entries]);

  // Warn in "Add Missing Entry" modal when selected employee+date already has an entry
  const newEntryDuplicateWarning = useMemo(() => {
    if (!form.employeeId || !form.clockIn) return null;
    const newDate = form.clockIn.slice(0, 10);
    const match = entries.find(e =>
      String(e.employeeId) === String(form.employeeId) &&
      entryDateKey(e.clockIn) === newDate
    );
    if (!match) return null;
    const emp = employees.find(em => String(em.id) === String(form.employeeId));
    return `${emp ? `${emp.firstName} ${emp.lastName}` : 'This employee'} already has an entry on ${fmtDate(match.clockIn)}.`;
  }, [form.employeeId, form.clockIn, entries, employees]);

  const openEdit = (entry) => {
    setForm({
      clockIn: toLocalDatetimeInput(entry.clockIn),
      clockOut: toLocalDatetimeInput(entry.clockOut),
      breakMinutes: entry.breakMinutes,
      adjustNote: '',
    });
    setSaveErr('');
    setEditModal(entry);
  };

  const openNew = () => {
    setForm({ employeeId: '', clockIn: '', clockOut: '', breakMinutes: 60, adjustNote: 'Manual entry by admin' });
    setSaveErr('');
    setEmpSearch('');
    setEmpDropOpen(false);
    setNewModal(true);
  };

  const handleEdit = async () => {
    if (!form.adjustNote?.trim()) { setSaveErr('Adjustment note is required'); return; }
    setSaving(true);
    try {
      const payload = {
        clockIn: form.clockIn ? new Date(form.clockIn).toISOString() : undefined,
        clockOut: form.clockOut ? new Date(form.clockOut).toISOString() : null,
        breakMinutes: Number(form.breakMinutes),
        adjustNote: form.adjustNote,
      };
      await timeTrackingService.updateEntry(editModal.id, payload);
      setEditModal(null);
      load();
    } catch (err) {
      setSaveErr(err?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleNew = async () => {
    if (!form.employeeId) { setSaveErr('Employee is required'); return; }
    if (!form.clockIn)    { setSaveErr('Clock-in time is required'); return; }
    setSaving(true);
    try {
      await timeTrackingService.createEntry({
        employeeId: form.employeeId,
        clockIn: new Date(form.clockIn).toISOString(),
        clockOut: form.clockOut ? new Date(form.clockOut).toISOString() : null,
        breakMinutes: Number(form.breakMinutes),
        adjustNote: form.adjustNote || 'Manual entry by admin',
      });
      setNewModal(false);
      load();
    } catch (err) {
      setSaveErr(err?.response?.data?.error || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    try {
      await timeTrackingService.deleteEntry(entry.id);
      setDeleteConfirm(null);
      load();
    } catch { /* ignore */ }
  };

  const totalShifts = entries.filter(e => e.clockOut).length;
  const totalNetHours = entries.reduce((sum, e) => sum + (e.netHours || 0), 0);
  const reviewCount = entries.filter(e => e.needsReview).length;
  const flaggedCount = entries.filter(e => {
    const isDup = duplicateDayKeys.has(`${e.employeeId}-${entryDateKey(e.clockIn)}`);
    return isDup || (e.netHours > 12);
  }).length;

  function rowStyle(e) {
    if (e.needsReview) return { background: '#fff0d6', borderLeft: '3px solid #fd7e14' };
    const isDup = duplicateDayKeys.has(`${e.employeeId}-${entryDateKey(e.clockIn)}`);
    const isLong = e.netHours != null && e.netHours > 12;
    if (isDup || isLong) return { background: '#fde8e8' };
    if (!e.clockOut)     return { background: '#fff9e6' };
    return undefined;
  }

  return (
    <div className="container mt-4">
      <TimeTrackingNav />

      <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
        <h5 className="mb-0">Timecards</h5>
        <button className="btn btn-outline-primary btn-sm" onClick={openNew}>+ Add Missing Entry</button>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-auto">
              <label className="form-label mb-1 small d-block">&nbsp;</label>
              <div className="btn-group btn-group-sm">
                <button className="btn btn-outline-secondary" onClick={() => { const p = getPeriodDates(payPeriodType, 0); setFrom(p.from); setTo(p.to); }}>Current Period</button>
                <button className="btn btn-outline-secondary" onClick={() => { const p = getPeriodDates(payPeriodType, -1); setFrom(p.from); setTo(p.to); }}>Previous</button>
              </div>
            </div>
            <div className="col-auto">
              <label className="form-label mb-1 small">From</label>
              <input className="form-control form-control-sm" type="date" value={from} onChange={e => setFrom(e.target.value)} />
            </div>
            <div className="col-auto">
              <label className="form-label mb-1 small">To</label>
              <input className="form-control form-control-sm" type="date" value={to} onChange={e => setTo(e.target.value)} />
            </div>
            <div className="col-auto">
              <label className="form-label mb-1 small">Employee</label>
              <select className="form-select form-select-sm" style={{ minWidth: 160 }} value={empId} onChange={e => setEmpId(e.target.value)}>
                <option value="">All Employees</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
              </select>
            </div>
            <div className="col-auto d-flex align-items-center gap-2">
              <div className="form-check mb-0">
                <input className="form-check-input" type="checkbox" id="openOnly" checked={openOnly} onChange={e => setOpenOnly(e.target.checked)} />
                <label className="form-check-label small" htmlFor="openOnly">Open shifts only</label>
              </div>
              <button className="btn btn-sm btn-secondary" onClick={load}>Refresh</button>
            </div>
          </div>
        </div>
      </div>

      {reviewCount > 0 && (
        <div className="alert py-2 px-3 mb-2 small" style={{ background: '#fff0d6', borderColor: '#fd7e14', color: '#7a3e00', borderLeft: '4px solid #fd7e14' }}>
          <strong>🕐 {reviewCount} {reviewCount === 1 ? 'entry needs' : 'entries need'} review:</strong> highlighted in orange below — clock-out recorded remotely after a forgotten punch. Correct the clock-out time, then save to clear the flag.
        </div>
      )}
      {flaggedCount > 0 && (
        <div className="alert alert-danger py-2 px-3 mb-3 small">
          <strong>⚠ {flaggedCount} flagged {flaggedCount === 1 ? 'entry' : 'entries'}:</strong> highlighted in red below — possible duplicates or shifts over 12 hours.
        </div>
      )}

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" /></div>
      ) : entries.length === 0 ? (
        <div className="text-muted text-center py-5">No time entries found for the selected filters.</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-hover align-middle table-sm">
              <thead className="table-light">
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Clock In</th>
                  <th>Clock Out</th>
                  <th>Break</th>
                  <th>Net Hours</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id} style={rowStyle(e)}>
                    <td className="fw-semibold">{e.Employee ? `${e.Employee.firstName} ${e.Employee.lastName}` : '—'}</td>
                    <td>{fmtDate(e.clockIn)}</td>
                    <td>{fmtTime(e.clockIn)}</td>
                    <td>{fmtTime(e.clockOut)}</td>
                    <td>{e.appliedBreak != null ? `${e.appliedBreak} min` : '—'}</td>
                    <td className={e.netHours > 12 ? 'fw-bold text-danger' : ''}>
                      {e.netHours != null ? e.netHours.toFixed(2) : '—'}
                    </td>
                    <td>
                      {e.needsReview
                        ? <span className="badge" style={{ background: '#fd7e14', color: '#fff' }}>Review Needed</span>
                        : !e.clockOut
                          ? <span className="badge bg-warning text-dark">Open</span>
                          : duplicateDayKeys.has(`${e.employeeId}-${entryDateKey(e.clockIn)}`)
                            ? <span className="badge bg-danger">Duplicate</span>
                            : e.adjustedBy
                              ? <span
                                  className="badge bg-info text-dark"
                                  style={{ cursor: 'help' }}
                                  title={`Adjusted by ${adjustedByName(e)}\nReason: ${e.adjustNote || '(no reason given)'}`}
                                >
                                  Adjusted
                                </span>
                              : <span className="badge bg-light text-secondary border">Closed</span>}
                    </td>
                    <td>
                      <div className="d-flex gap-1 justify-content-end">
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => openEdit(e)}>Edit</button>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => setDeleteConfirm(e)}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex gap-4 text-muted small mt-2 flex-wrap">
            <span>Closed shifts: <strong>{totalShifts}</strong></span>
            <span>Total net hours: <strong>{totalNetHours.toFixed(2)} hrs</strong></span>
            <span style={{ color: '#ffc107' }}>■ Open shift</span>
            <span style={{ color: '#f8d7da' }}>■ Duplicate day or &gt;12 hrs</span>
          </div>
        </>
      )}

      {/* ── Edit Modal ────────────────────────────────────────────────────── */}
      {editModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Entry — {editModal.Employee?.firstName} {editModal.Employee?.lastName}</h5>
                <button className="btn-close" onClick={() => setEditModal(null)} />
              </div>
              <div className="modal-body">
                {saveErr && <div className="alert alert-danger py-2">{saveErr}</div>}
                {editModal.adjustedBy && (
                  <div className="alert alert-secondary py-2 px-3 mb-3 small">
                    Last adjusted by <strong>{adjustedByName(editModal)}</strong>: "{editModal.adjustNote || '(no reason given)'}"
                  </div>
                )}
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label">Clock In</label>
                    <input className="form-control" type="datetime-local" value={form.clockIn} onChange={e => setForm(f => ({ ...f, clockIn: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Clock Out</label>
                    <input className="form-control" type="datetime-local" value={form.clockOut} onChange={e => setForm(f => ({ ...f, clockOut: e.target.value }))} />
                    <div className="form-text">Leave blank if shift is still open</div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Break (minutes)</label>
                    <input className="form-control" type="number" min="0" value={form.breakMinutes} onChange={e => setForm(f => ({ ...f, breakMinutes: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Reason for adjustment *</label>
                    <input className="form-control" placeholder="e.g. Employee forgot to clock out" value={form.adjustNote} onChange={e => setForm(f => ({ ...f, adjustNote: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleEdit} disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1" />}Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── New Entry Modal ────────────────────────────────────────────────── */}
      {newModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Missing Entry</h5>
                <button className="btn-close" onClick={() => setNewModal(false)} />
              </div>
              <div className="modal-body">
                {saveErr && <div className="alert alert-danger py-2">{saveErr}</div>}
                {newEntryDuplicateWarning && (
                  <div className="alert alert-warning py-2 px-3 mb-2 small">
                    <strong>⚠ Possible duplicate:</strong> {newEntryDuplicateWarning} You can still save if this is intentional.
                  </div>
                )}
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Employee *</label>
                    <div className="position-relative">
                      <input
                        className="form-control"
                        placeholder="Search employee…"
                        value={empSearch}
                        autoComplete="off"
                        onChange={e => {
                          setEmpSearch(e.target.value);
                          setEmpDropOpen(true);
                          if (!e.target.value) setForm(f => ({ ...f, employeeId: '' }));
                        }}
                        onFocus={() => setEmpDropOpen(true)}
                        onBlur={() => setTimeout(() => setEmpDropOpen(false), 150)}
                      />
                      {empDropOpen && (() => {
                        const q = empSearch.toLowerCase();
                        const filtered = employees.filter(e => e.status === 'active' && `${e.firstName} ${e.lastName}`.toLowerCase().includes(q));
                        if (!filtered.length) return null;
                        return (
                          <ul className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1050, maxHeight: 200, overflowY: 'auto', top: '100%' }}>
                            {filtered.map(e => (
                              <li
                                key={e.id}
                                className={`list-group-item list-group-item-action py-1 px-3 small${String(form.employeeId) === String(e.id) ? ' active' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onMouseDown={() => {
                                  setForm(f => ({ ...f, employeeId: e.id }));
                                  setEmpSearch(`${e.firstName} ${e.lastName}`);
                                  setEmpDropOpen(false);
                                }}
                              >
                                {e.firstName} {e.lastName}
                              </li>
                            ))}
                          </ul>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Clock In *</label>
                    <input className="form-control" type="datetime-local" value={form.clockIn} onChange={e => setForm(f => ({ ...f, clockIn: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Clock Out</label>
                    <input className="form-control" type="datetime-local" value={form.clockOut} onChange={e => setForm(f => ({ ...f, clockOut: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Break (minutes)</label>
                    <input className="form-control" type="number" min="0" value={form.breakMinutes} onChange={e => setForm(f => ({ ...f, breakMinutes: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Note</label>
                    <input className="form-control" value={form.adjustNote} onChange={e => setForm(f => ({ ...f, adjustNote: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setNewModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleNew} disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1" />}Add Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ─────────────────────────────────────────────────── */}
      {deleteConfirm && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content">
              <div className="modal-body text-center py-4">
                <div style={{ fontSize: '2rem' }}>🗑️</div>
                <div className="fw-semibold mt-2">Delete this entry?</div>
                <div className="text-muted small mt-1">
                  {deleteConfirm.Employee?.firstName} {deleteConfirm.Employee?.lastName} — {fmtDate(deleteConfirm.clockIn)}
                </div>
                <div className="text-danger small mt-1">This cannot be undone.</div>
              </div>
              <div className="modal-footer justify-content-center">
                <button className="btn btn-secondary btn-sm" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
